import { useState } from "react";
import { Plus, Pencil, Trash2, FlaskConical, ExternalLink } from "lucide-react";
import { Table, Button, Modal, Form, Input, Select, Tag, Popconfirm, message, Spin } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import type { ColumnType } from "antd/es/table";
import { ICoshhSubstance } from "../../../types/api/coshh";
import { useCoshhList, useCoshhCreate, useCoshhUpdate, useCoshhDelete } from "../../../hooks/api/coshh/hooks";

const PPE_OPTIONS = ["Gloves", "Goggles", "Respirator", "Lab coat", "Face shield", "Apron", "Boots"];

function CoshhModal({
  open,
  substance,
  onClose,
}: {
  open: boolean;
  substance: ICoshhSubstance | null;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const isEdit = !!substance;

  const create = useCoshhCreate({
    onSuccess: () => { msgApi.success("Substance created."); onClose(); form.resetFields(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });
  const update = useCoshhUpdate({
    onSuccess: () => { msgApi.success("Substance updated."); onClose(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const loading = create.isPending || update.isPending;

  const onFinish = (vals: any) => {
    if (isEdit) {
      update.mutate({ id: substance.id, ...vals });
    } else {
      create.mutate(vals);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => { onClose(); form.resetFields(); }}
      title={<span className="font-semibold">{isEdit ? "Edit Substance" : "Add COSHH Substance"}</span>}
      width={560}
      footer={[
        <Button key="cancel" onClick={() => { onClose(); form.resetFields(); }}>Cancel</Button>,
        <Button key="save" type="primary" loading={loading} onClick={() => form.submit()}>
          {isEdit ? "Update" : "Add Substance"}
        </Button>,
      ]}
    >
      {ctx}
      <Form
        form={form}
        layout="vertical"
        size="middle"
        className="pt-2"
        onFinish={onFinish}
        initialValues={substance ?? { is_active: 1 }}
        key={substance?.id ?? "new"}
      >
        <Form.Item name="name" label="Substance Name" rules={[{ required: true, message: "Name required" }]}>
          <Input placeholder="e.g. Cement, White Spirit" />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="cas_number" label="CAS Number">
            <Input placeholder="e.g. 65997-15-1" />
          </Form.Item>
          <Form.Item name="hazard_class" label="Hazard Class">
            <Input placeholder="e.g. Skin Irritant, Corrosive" />
          </Form.Item>
        </div>
        <Form.Item name="sds_url" label="SDS / Data Sheet URL">
          <Input placeholder="https://..." />
        </Form.Item>
        <Form.Item name="ppe_required" label="PPE Required">
          <Select mode="multiple" placeholder="Select PPE items" options={PPE_OPTIONS.map((p) => ({ value: p, label: p }))} />
        </Form.Item>
        <Form.Item name="storage_requirements" label="Storage Requirements">
          <Input.TextArea rows={2} placeholder="e.g. Store in cool, dry place away from ignition sources" />
        </Form.Item>
        <Form.Item name="disposal_method" label="Disposal Method">
          <Input.TextArea rows={2} placeholder="e.g. Dispose via licensed waste contractor" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const CoshhPage = () => {
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ICoshhSubstance | null>(null);
  const [msgApi, ctx] = message.useMessage();

  const { data: res, isLoading, refetchWithQuery } = useCoshhList({ page: 1, limit: 30 });
  const substances: ICoshhSubstance[] = (res?.data as any) ?? [];
  const total = Number(res?.extra?.total ?? 0);

  const deleteSubstance = useCoshhDelete({
    onSuccess: () => msgApi.success("Substance removed."),
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const columns: ColumnType<ICoshhSubstance>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (v, r) => (
        <div>
          <div className="font-medium text-slate-800 text-sm">{v}</div>
          {r.cas_number && <div className="text-xs text-slate-400">CAS: {r.cas_number}</div>}
        </div>
      ),
    },
    {
      title: "Hazard Class",
      dataIndex: "hazard_class",
      key: "hazard_class",
      render: (v) => v ? <Tag color="orange">{v}</Tag> : <span className="text-slate-300 text-xs">—</span>,
    },
    {
      title: "PPE",
      dataIndex: "ppe_required",
      key: "ppe_required",
      render: (v) => {
        const items = Array.isArray(v) ? v : (typeof v === "string" ? JSON.parse(v || "[]") : []);
        return items.length > 0
          ? <div className="flex flex-wrap gap-1">{items.slice(0, 3).map((p: string) => <Tag key={p} color="blue" className="text-xs">{p}</Tag>)}{items.length > 3 && <Tag className="text-xs">+{items.length - 3}</Tag>}</div>
          : <span className="text-slate-300 text-xs">—</span>;
      },
    },
    {
      title: "SDS",
      dataIndex: "sds_url",
      key: "sds_url",
      render: (v) => v
        ? <a href={v} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 text-xs hover:underline"><ExternalLink size={11} />View SDS</a>
        : <span className="text-slate-300 text-xs">—</span>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
      width: 90,
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, r) => (
        <div className="flex gap-1.5">
          <Button size="small" icon={<Pencil size={11} />} onClick={() => { setEditing(r); setModalOpen(true); }} />
          <Popconfirm title="Delete this substance?" onConfirm={() => deleteSubstance.mutate(r.id)} okText="Delete" okType="danger">
            <Button size="small" danger icon={<Trash2 size={11} />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">COSHH Substance Library</h1>
          <p className="text-slate-500 text-sm">{total} substances · Control of Substances Hazardous to Health</p>
        </div>
        <ButtonWrapper type="primary" icon={<Plus size={15} />} onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-2">
          Add Substance
        </ButtonWrapper>
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); refetchWithQuery({ page: 1, search: e.target.value }); }}
          placeholder="Search substances by name or CAS number…"
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg w-80 bg-white focus:outline-none focus:border-primary"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={substances}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 30, total, showTotal: (t) => `${t} substances`, showSizeChanger: false }}
          scroll={{ x: 800 }}
        />
      </div>

      <CoshhModal
        open={modalOpen}
        substance={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
      />
    </div>
  );
};

export default CoshhPage;
